/**
 * Everything that ever happened to one register row.
 *
 * The trail has been written since the first load — every approved change, every direct
 * edit, every re-load that matched an existing row — and until now nothing read it back for
 * a *row*. The builder reads it for a register's schema, which answers "what was this
 * register allowed to record"; this answers the question people actually ask, which is
 * "why does RSK-0001 say 25 when it said 16 last month, and who decided that".
 *
 * No new endpoint was needed. `GET /audit` has taken `entity_table` and `entity_id` since it
 * shipped, and `entity_table` accepts a register slug. The screen was the missing half.
 *
 * Three things this screen refuses to do, each for the same reason — an audit trail people
 * do not believe is worth nothing:
 *
 *   - It shows the values, not a summary of them. "3 fields changed" is a thing you have to
 *     take on trust; `16 → 25` is not.
 *   - It shows entries with no field changes too. A re-load that matched the row and altered
 *     nothing is evidence about that month, and hiding it would make the trail look sparser
 *     than the record is.
 *   - It never paginates silently. Fifty entries, and it says so when there are more.
 */

import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { ColumnInfo, RegisterInfo } from '@/api/types'
import { navigate } from '@/app/routes'
import { Icon } from '@/components/Icon'
import { Empty, Failed, Skeleton } from '@/components/states'
import { fieldLabel } from '@/lib/labels'
import { registerLabel } from '@/lib/registers'

type AuditEntry = {
  id: string
  at: string
  actor_id: string | null
  actor_name: string | null
  action: string
  entity_table: string
  entity_id: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  via_ai: boolean
}

const LIMIT = 50

/** One side of a change, as a reader should see it. */
function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return String(value)
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * The fields that actually moved.
 *
 * Both sides are walked, not just `after`: a field cleared to null is a change, and one that
 * only appears in `before` would otherwise vanish from the very entry that removed it.
 */
function changed(entry: AuditEntry): { name: string; from: unknown; to: unknown }[] {
  const before = entry.before ?? {}
  const after = entry.after ?? {}
  const names = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()
  return names
    .filter((name) => show(before[name]) !== show(after[name]))
    .map((name) => ({ name, from: before[name], to: after[name] }))
}

/** The register's own label for a column, falling back to the humanised key. */
function columnLabel(columns: ColumnInfo[], name: string): string {
  return columns.find((column) => column.name === name)?.label ?? fieldLabel(name)
}

function actionLabel(t: TFunction, action: string): string {
  return t(`history.action.${action}`, { defaultValue: fieldLabel(action) })
}

export function RowHistory({ slug, rowId }: { slug: string; rowId: string }) {
  const { t, i18n } = useTranslation()

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: ({ signal }) => api.get<RegisterInfo[]>('/registers', signal),
    staleTime: Infinity,
  })

  const trail = useQuery({
    queryKey: ['audit', slug, rowId],
    queryFn: ({ signal }) =>
      api.get<{ items: AuditEntry[]; meta: { total: number } }>(
        `/audit?entity_table=${encodeURIComponent(slug)}&entity_id=${encodeURIComponent(rowId)}` +
          `&limit=${LIMIT}`,
        signal,
      ),
  })

  const register = (registers.data ?? []).find((entry) => entry.slug === slug)
  const columns = register?.columns ?? []
  const label = registerLabel(t, slug, register?.label ?? slug)

  const when = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  const items = trail.data?.items ?? []
  const total = trail.data?.meta.total ?? 0

  return (
    <main className="page" data-testid="row-history">
      <nav className="crumbs" aria-label={t('a11y.breadcrumb')}>
        <button
          type="button"
          className="crumbs__link"
          onClick={() => navigate({ name: 'projects' })}
        >
          {label}
        </button>
        <Icon name="chevronEnd" size={13} strokeWidth={2} className="crumbs__sep" />
        <span className="crumbs__here">{t('history.title')}</span>
      </nav>

      <header className="page__header">
        <div>
          <h1 className="page__title">{t('history.title')}</h1>
          <p className="page__subtitle">{t('history.subtitle')}</p>
        </div>
      </header>

      <section className="card card--pad">
        {trail.isPending ? (
          <Skeleton rows={4} />
        ) : trail.isError ? (
          <Failed
            message={
              trail.error instanceof ApiError ? trail.error.friendly : String(trail.error)
            }
            onRetry={() => void trail.refetch()}
          />
        ) : items.length === 0 ? (
          // Not "no history". A row always has at least the entry that created it, so an
          // empty trail means this row is not one — a wrong id, or a register that does not
          // hold it — and saying "nothing yet" would send somebody looking for a bug in the
          // audit trail rather than in the address they followed.
          <Empty title={t('history.none')} hint={t('history.noneHint')} />
        ) : (
          <>
            <ol className="trail" data-testid="trail">
              {items.map((entry) => {
                const fields = changed(entry)
                return (
                  <li key={entry.id} className="trail__entry" data-action={entry.action}>
                    <span className="trail__mark" aria-hidden="true" />
                    <div className="trail__body">
                      <p className="trail__head">
                        <span className="trail__action">{actionLabel(t, entry.action)}</span>
                        {entry.via_ai ? (
                          <span className="chip chip--ai">{t('application.viaAi')}</span>
                        ) : null}
                        <span className="trail__when numeric">{when.format(new Date(entry.at))}</span>
                      </p>

                      {fields.length > 0 ? (
                        <table className="diff">
                          <tbody>
                            {fields.map((field) => (
                              <tr key={field.name} data-field={field.name}>
                                <th scope="row">{columnLabel(columns, field.name)}</th>
                                <td className="diff__before">
                                  <span>{show(field.from)}</span>
                                </td>
                                <td className="diff__arrow" aria-hidden="true">
                                  <Icon name="chevronEnd" size={13} strokeWidth={2.4} />
                                </td>
                                <td className="diff__after">
                                  <span>{show(field.to)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="trail__none">{t('history.noFields')}</p>
                      )}

                      {/* Who, by name. Not their role: the row does not record the role
                          held at the time, and today's would attribute authority the person
                          may not have had. No actor at all is the loader or the scheduler,
                          and that is worth saying rather than leaving blank. */}
                      <p className="trail__actor">
                        {entry.actor_name ?? t('history.system')}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ol>

            {total > items.length ? (
              <p className="card__note" data-testid="trail-more">
                {t('history.more', { shown: items.length, total })}
              </p>
            ) : null}
          </>
        )}
      </section>
    </main>
  )
}
